import { useQueryClient } from '@tanstack/react-query';
import { FileSignature, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { PaginatedResponse, Proposal } from '@/api/commercial/proposals';
import { ProposalStatus, proposalApi } from '@/api/commercial/proposals';
import type { ProposalContractViewModel } from '@/components/commercial/proposals/contracts/proposal-contract-types';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { isApiError } from '@/lib/api';
import { formatLgpdCnpj, formatLgpdCpf } from '@/lib/format-lgpd';

export interface ProposalTableContractsConfig {
  getContractsForProposal: (proposal: Proposal) => ProposalContractViewModel[];
  onViewProposalContracts: (proposal: Proposal) => void;
}

interface ProposalTableProps {
  data: PaginatedResponse<Proposal>;
  currentPage: number;
  onPageChange: (page: number) => void;
  contracts?: ProposalTableContractsConfig;
}

const STATUS_LABEL: Record<ProposalStatus, { label: string; color: string }> = {
  [ProposalStatus.OPEN]: {
    label: 'Aberta',
    color: 'bg-yellow-100 text-yellow-800',
  },
  [ProposalStatus.PENDING_ACCEPT]: {
    label: 'Aguardando aceite',
    color: 'bg-blue-100 text-blue-800',
  },
  [ProposalStatus.ACCEPTED_PENDING_PAYMENT]: {
    label: 'Aceita – Aguardando pagamento',
    color: 'bg-yellow-100 text-yellow-800',
  },
  [ProposalStatus.PENDING_CREDIT]: {
    label: 'Aguardando Análise de Crédito',
    color: 'bg-blue-100 text-blue-800',
  },
  [ProposalStatus.FINISHED]: {
    label: 'Finalizada',
    color: 'bg-green-100 text-green-800',
  },
  [ProposalStatus.CANCELED]: {
    label: 'Cancelada',
    color: 'bg-red-100 text-red-800',
  },
  [ProposalStatus.REFUSED]: {
    label: 'Recusada',
    color: 'bg-red-100 text-red-800',
  },
  [ProposalStatus.EXPIRED]: {
    label: 'Expirada',
    color: 'bg-orange-100 text-orange-800',
  },
};

export function ProposalTable({ data, currentPage, onPageChange, contracts }: ProposalTableProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = { fullLgpdAccess: true };
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [isReopening, setIsReopening] = useState(false);

  const formatDocument = (cpfCnpj: string): string => {
    if (!cpfCnpj) return '-';
    const value = cpfCnpj.replace(/\D/g, '');
    const isCpf = value.length === 11;

    if (isCpf) {
      return formatLgpdCpf(value, permissions);
    } else {
      return formatLgpdCnpj(value, permissions);
    }
  };

  const formatDateTime = (value?: string | Date): string => {
    if (!value) return '-';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const resolveStatus = (status?: ProposalStatus | string) => {
    if (!status) {
      return { label: '-', color: 'bg-gray-100 text-gray-800' };
    }

    if (typeof status === 'string' && status in STATUS_LABEL) {
      return STATUS_LABEL[status as ProposalStatus];
    }

    return (
      STATUS_LABEL[status as ProposalStatus] ?? {
        label: status,
        color: 'bg-gray-100 text-gray-800',
      }
    );
  };

  const docs = data.docs ?? [];

  const navigateToEdit = (proposalId: string) => {
    navigate(`/commercial/proposal/form/update/${proposalId}`);
  };

  const handleEditClick = (proposal: Proposal) => {
    if (proposal.status === ProposalStatus.OPEN) {
      navigateToEdit(proposal.id);
      return;
    }

    if (
      proposal.status === ProposalStatus.PENDING_CREDIT ||
      proposal.status === ProposalStatus.PENDING_ACCEPT ||
      proposal.status === ProposalStatus.REFUSED ||
      proposal.status === ProposalStatus.EXPIRED
    ) {
      setSelectedProposal(proposal);
    }
  };

  const handleConfirmReopen = async () => {
    if (!selectedProposal) return;

    setIsReopening(true);
    try {
      const reopenedProposal = await proposalApi.reopen(selectedProposal.id);

      await queryClient.invalidateQueries({ queryKey: ['proposal', selectedProposal.id] });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });

      setSelectedProposal(null);
      navigateToEdit(reopenedProposal.id);
    } catch (error) {
      const description = isApiError(error)
        ? error.response?.data.message
        : 'Nao foi possivel reabrir a proposta. Tente novamente.';

      toast({
        title: 'Erro ao reabrir proposta',
        description,
        variant: 'destructive',
      });
    } finally {
      setIsReopening(false);
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PROPOSTA</TableHead>
              <TableHead>CLIENTE</TableHead>
              <TableHead>CPF/CNPJ</TableHead>
              <TableHead>DATA DE ABERTURA</TableHead>
              <TableHead>VENDEDOR</TableHead>
              <TableHead>STATUS</TableHead>
              <TableHead className="text-right">AÇÃO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Nenhuma proposta encontrada
                </TableCell>
              </TableRow>
            ) : (
              docs.map((proposal) => {
                const status = resolveStatus(proposal.status);
                const canEdit =
                  proposal.status === ProposalStatus.OPEN ||
                  proposal.status === ProposalStatus.PENDING_CREDIT ||
                  proposal.status === ProposalStatus.PENDING_ACCEPT ||
                  proposal.status === ProposalStatus.REFUSED ||
                  proposal.status === ProposalStatus.EXPIRED;
                const proposalContracts = contracts?.getContractsForProposal(proposal) ?? [];
                const canViewContracts = proposalContracts.length > 0;
                return (
                  <TableRow key={proposal.id}>
                    <TableCell>{proposal.seq}</TableCell>
                    <TableCell>
                      <span className="font-medium">{proposal.clientName || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDocument(proposal.clientCpfCnpj)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDateTime(proposal.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{proposal.userName || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider>
                        <div className="flex justify-end gap-1">
                          {canViewContracts && contracts && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                  onClick={() => contracts.onViewProposalContracts(proposal)}
                                >
                                  <FileSignature className="size-4" />
                                  <span className="sr-only">
                                    Ver contratos da proposta {proposal.seq}
                                  </span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Contrato</TooltipContent>
                            </Tooltip>
                          )}
                          {canEdit && (
                            <Button
                              title="Editar proposta"
                              variant="ghost"
                              className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              onClick={() => handleEditClick(proposal)}
                              disabled={isReopening && selectedProposal?.id === proposal.id}
                            >
                              {isReopening && selectedProposal?.id === proposal.id ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Pencil className="size-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.pages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />

      <AlertDialog
        open={selectedProposal !== null}
        onOpenChange={(open) => {
          if (!open && !isReopening) {
            setSelectedProposal(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Editar proposta</AlertDialogTitle>
            <AlertDialogDescription>
              Para editar esta proposta, ela sera reaberta e voltara para o status Aberta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReopening}>Cancelar</AlertDialogCancel>
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={handleConfirmReopen}
              disabled={isReopening}
            >
              {isReopening && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isReopening ? 'Reabrindo...' : 'Confirmar'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
